import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles, ArrowRight, Tv, Vote, Smartphone, Signal, Briefcase, HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import { fetchSwipeDeck, type SwipeCard } from '../lib/api';

// Minimal local copies of the swipe-page helpers so the section is self-contained
// (no coupling to the page). Generic iconography only — never a real brand mark.
const STREAMING_KEYS = new Set([
  'netflix', 'hulu', 'disney_plus', 'max', 'prime_video', 'peacock', 'paramount_plus', 'apple_tv_plus',
]);
function cardIcon(key: string): LucideIcon {
  if (STREAMING_KEYS.has(key)) return Tv;
  switch (key) {
    case 'voter_registration': return Vote;
    case 'smartphone_type': return Smartphone;
    case 'cell_plan': return Signal;
    case 'decision_maker': return Briefcase;
    default: return HelpCircle;
  }
}
const VALUE_LABELS: Record<string, string> = {
  yes: 'Yes', no: 'No', iphone: 'iPhone', android: 'Android', pre_paid: 'Pre-paid', contract: 'Contract',
};
function optLabel(v: string): string {
  return VALUE_LABELS[v] ?? v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Dashboard entry point for Swipe. Reflects REAL deck state via GET /api/swipe/deck
 * (no new endpoint). Renders nothing until the count resolves, so the dashboard never
 * blocks on it. HIDDEN when the deck is empty (allSet / 0 cards): the deck is
 * one-time-until-answered (assembleDeck filters answered keys; no daily refill), so an
 * empty deck is dead space — and the section self-reactivates the moment the catalog
 * grows and the count goes >0 again. Also hidden on error (never a broken section).
 */
export default function SwipeDashboardSection() {
  const [state, setState] = useState<'loading' | 'ready' | 'hidden'>('loading');
  const [cards, setCards] = useState<SwipeCard[]>([]);

  useEffect(() => {
    let alive = true;
    fetchSwipeDeck()
      .then(({ cards, allSet }) => {
        if (!alive) return;
        if (allSet || cards.length === 0) setState('hidden');
        else {
          setCards(cards);
          setState('ready');
        }
      })
      .catch(() => alive && setState('hidden'));
    return () => {
      alive = false;
    };
  }, []);

  if (state !== 'ready') return null;

  const count = cards.length;
  const preview = cards[0]!;
  const Icon = cardIcon(preview.key);
  const leftLabel = optLabel(preview.answerValueMap.left);
  const rightLabel = optLabel(preview.answerValueMap.right);

  return (
    <section>
      <h2 className="text-2xl font-bold font-display text-[#111827] flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-primary" />
        Unlock better surveys
      </h2>

      <Link
        to="/swipe"
        className="group block rounded-3xl bg-gradient-to-br from-primary-light via-white to-white ring-1 ring-primary/15 shadow-[0_10px_34px_-14px_rgba(91,79,232,0.30)] hover:shadow-[0_16px_44px_-14px_rgba(91,79,232,0.40)] transition-shadow overflow-hidden"
      >
        <div className="flex flex-col sm:flex-row items-center gap-6 p-6">
          {/* Copy + CTA */}
          <div className="flex-1 text-center sm:text-left">
            <h3 className="text-lg font-bold font-display text-[#111827] leading-snug">
              A few quick answers = better-matched surveys
            </h3>
            <p className="text-sm text-[#6B7280] mt-1.5 leading-relaxed">
              Tell us a little more so we can match you with better-paying, more relevant
              surveys.
            </p>
            <span className="inline-flex items-center gap-2 mt-4 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-primary/30 group-hover:bg-primary-dark transition-colors">
              {count} question{count === 1 ? '' : 's'} remaining
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </div>

          {/* Static, non-interactive preview of the real next card. A gentle
              idle float/tilt breathes so the card reads as interactive. */}
          <motion.div
            aria-hidden="true"
            className="shrink-0 w-56"
            animate={{ y: [0, -6, 0], rotate: [2.5, 1, 2.5] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="rounded-2xl bg-white shadow-[0_20px_44px_-16px_rgba(15,23,42,0.38)] p-4 flex flex-col gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-light to-primary/10 flex items-center justify-center ring-1 ring-primary/10">
                <Icon className="w-6 h-6 text-primary" strokeWidth={1.75} />
              </div>
              <p className="text-sm font-bold font-display text-navy leading-snug min-h-[2.5rem]">
                {preview.prompt}
              </p>
              <div className="flex items-center justify-between text-[11px] font-semibold text-gray-400 pt-1">
                <span>← {leftLabel}</span>
                <span>{rightLabel} →</span>
              </div>
            </div>
          </motion.div>
        </div>
      </Link>
    </section>
  );
}
